import { useState, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  TextField,
  Typography,
} from "@mui/material"
import { first, find, get, isNil, sortBy } from "lodash"

export default function AIInputForms({
  portfolios = [],
  models = [],
  onRunQuery,
  isLoading,
}) {
  // Always initialize with empty string, never undefined
  const [portfolioAlias, setPortfolioAlias] = useState()
  const [modelId, setModelId] = useState()
  const [question, setQuestion] = useState("")
  const [apiError, setApiError] = useState(null)

  const sortedPortfolios = useMemo(() =>
    sortBy(portfolios, "name"),
  [portfolios])

  const sortedModels = useMemo(() =>
    sortBy(models, "name"),
  [models])

  // Set default portfolioAlias if not already set
  useEffect(() => {
    if (isNil(portfolioAlias)) {
      const firstPortfolio = first(sortedPortfolios)
      if (firstPortfolio) {
        setPortfolioAlias(firstPortfolio.alias)
      }
    }
  }, [sortedPortfolios, portfolioAlias])

  // Set default model if not already set
  useEffect(() => {
    if (isNil(modelId)) {
      const firstModel = first(sortedModels)
      if (firstModel) {
        setModelId(firstModel.id)
      }
    }
  }, [sortedModels, modelId])

  const handlePortfolioChange = (event, newValue) => {
    setPortfolioAlias(newValue?.alias || "")
  }

  const handleModelChange = (event, newValue) => {
    setModelId(newValue?.id || "")
  }

  const handleQuestionChange = (event) => {
    const newValue = get(event, "target.value", "")
    setQuestion(newValue)
  }

  // Handle Enter key to run query
  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey && !isQueryRunButtonDisabled) {
      event.preventDefault()
      handleRunAiQuickQuery()
    }
  }

  const selectedPortfolio = find(sortedPortfolios, p => p.alias === portfolioAlias)

  const selectedModel = find(sortedModels, { id: modelId })

  const handleRunAiQuickQuery = () => {
    if (!question.trim()) return

    setApiError(null)

    // Pass the query parameters to the parent component
    onRunQuery({
      question,
      modelId,
      resourceId: portfolioAlias,
      resourceType: "PORTFOLIO",
    })
  }

  const isQueryRunButtonDisabled = !question.trim() || !portfolioAlias || !modelId || isLoading

  return (
    <div className="panel-content ai-quick-answers-context">
      <Typography variant="h6" gutterBottom>
        AI Quick Answers
      </Typography>

      <Autocomplete
        fullWidth
        options={sortedPortfolios}
        getOptionLabel={(option) => get(option, "name", "")}
        value={selectedPortfolio || null}
        onChange={handlePortfolioChange}
        disabled={isLoading}
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            label="Portfolio"
            margin="normal"
            className="field-bg"
          />
        )}
      />

      <Autocomplete
        fullWidth
        options={sortedModels}
        getOptionLabel={(option) => get(option, "name", "")}
        value={selectedModel || null}
        onChange={handleModelChange}
        disabled={isLoading}
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            label="Risk Model"
            margin="normal"
            className="field-bg"
          />
        )}
      />

      <Divider sx={{ my: 2 }} />
      <TextField
        fullWidth
        margin="normal"
        id="question-input"
        label="Your Question"
        variant="outlined"
        value={question}
        onChange={handleQuestionChange}
        onKeyDown={handleKeyDown}
        placeholder="e.g., What is the total cumulative percent return of my portfolio this year?"
        multiline
        rows={3}
        size="small"
        className="field-bg"
        disabled={isLoading}
      />

      <Box className="button-container">
        <Button
          variant="contained"
          color="primary"
          onClick={handleRunAiQuickQuery}
          disabled={isQueryRunButtonDisabled}
          loading={isLoading}
          loadingIndicator="Processing..."
        >
          Run Quick Query
        </Button>
      </Box>

      {apiError && (
        <Box className="error-message">
          <Typography variant="body2" color="error">
            Error: {apiError}
          </Typography>
        </Box>
      )}
    </div>
  )
}

AIInputForms.propTypes = {
  portfolios: PropTypes.array.isRequired,
  models: PropTypes.array.isRequired,
  onRunQuery: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
}
